import React, { useCallback, useEffect, useRef, useState } from "react";
import { graphql, PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import AuditLogsFilters from "../components/AuditLogsFilters";
import AuditLogsPreview from "../components/AuditLogsPreview";
import { SettingsPageHeader } from "../components/SettingsPageHeader";
import { useSettingsMobileHeader } from "../hooks/useSettingsMobileHeader";
import { AuditLogsRouteQuery, GraphQLAuditLogCategory } from "./__generated__/AuditLogsRouteQuery.graphql";

const auditLogsRouteQuery = graphql`
  query AuditLogsRouteQuery($category: GraphQLAuditLogCategory, $userSearchStr: String) {
    me {
      ...appMe
      organization {
        id
        auditLogs(category: $category, userSearchStr: $userSearchStr) {
          ...AuditLogsPreview_auditLogs
        }
      }
    }
  }
`;

function AuditLogsContent({
  queryRef,
  isSearching,
  onClearFilters
}: {
  queryRef: PreloadedQuery<AuditLogsRouteQuery>;
  isSearching: boolean;
  onClearFilters: () => void;
}) {
  const data = usePreloadedQuery(auditLogsRouteQuery, queryRef);

  if (!data.me?.organization) {
    return <div className="text-center py-8 text-muted-foreground">No organization found</div>;
  }

  return (
    <>
      {data.me.organization.auditLogs && (
        <AuditLogsPreview
          auditLogs={data.me.organization.auditLogs}
          isSearching={isSearching}
          onClearFilters={onClearFilters}
        />
      )}
    </>
  );
}

export default function AuditLogsRoute() {
  useSettingsMobileHeader("Audit Logs");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [userFilter, setUserFilter] = useState<string>("");
  const [isSearching, setIsSearching] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout>();

  const [queryRef, loadQuery] = useQueryLoader<AuditLogsRouteQuery>(auditLogsRouteQuery);

  // Load initial query
  useEffect(() => {
    loadQuery({
      category: null,
      userSearchStr: null,
    });
  }, [loadQuery]);

  // Debounce and reload query
  const debouncedLoadQuery = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    setIsSearching(true);
    debounceRef.current = setTimeout(() => {
      loadQuery({
        category: categoryFilter === "all" ? null : (categoryFilter as GraphQLAuditLogCategory),
        userSearchStr: userFilter || null,
      });
      setIsSearching(false);
    }, 300);
  }, [loadQuery, categoryFilter, userFilter]);

  // Trigger debounced query on filter changes
  useEffect(() => {
    debouncedLoadQuery();
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [debouncedLoadQuery]);

  const handleClearFilters = () => {
    setCategoryFilter("all");
    setUserFilter("");
  };

  return (
    <div className="flex-1 px-4 md:px-5 pt-4 pb-2">
      <div className="max-w-6xl">
        <SettingsPageHeader
          title="Audit Logs"
          tooltip="View activity logs for your organization."
        />

        <AuditLogsFilters
          categoryFilter={categoryFilter}
          setCategoryFilter={setCategoryFilter}
          userFilter={userFilter}
          setUserFilter={setUserFilter}
        />

        {queryRef ? (
          <React.Suspense fallback={<div className="text-center py-8 text-muted-foreground">Loading...</div>}>
            <AuditLogsContent
              queryRef={queryRef}
              isSearching={isSearching}
              onClearFilters={handleClearFilters}
            />
          </React.Suspense>
        ) : (
          <div className="text-center py-8 text-muted-foreground">Loading...</div>
        )}
      </div>
    </div>
  );
}
